import { useEffect, useRef, useState } from 'react'
import { ChevronDownIcon } from './icons.jsx'

// Accessible custom dropdown that replaces the native <select>, so the option
// list is styled and themed like the rest of the app instead of the OS popup.
//
// options: [{ value, label, disabled?, hint?, note? }]
//   hint  — a second, muted field beside the label ("President"): shown in the
//           list and, after a middle dot, in the closed button.
//   note  — a muted tag on the list row only ("no email on file"): a fact
//           about the option the reader should see before choosing it.
// size: 'md' (default) or 'sm' — the compact form for inline use on a card.
// Controlled via `value` / `onChange(value)`.
export default function Select({
  value,
  onChange,
  options,
  disabled = false,
  placeholder = 'Select…',
  ariaLabel,
  buttonClassName = '',
  size = 'md',
  // Told whenever the list opens or closes — for a host whose frame clips
  // overflow (a rounded card) and has to let the listbox out while it shows.
  onOpenChange = null,
}) {
  const small = size === 'sm'
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const rootRef = useRef(null)
  const listRef = useRef(null)
  const typeahead = useRef({ str: '', time: 0 })
  const openChangeRef = useRef(onOpenChange)
  openChangeRef.current = onOpenChange
  useEffect(() => {
    if (typeof openChangeRef.current === 'function') openChangeRef.current(open)
  }, [open])

  const selectedIndex = options.findIndex((o) => o.value === value)
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null

  // Close on outside pointer.
  useEffect(() => {
    if (!open) return
    function onPointer(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    return () => document.removeEventListener('mousedown', onPointer)
  }, [open])

  // On open, highlight the selected option.
  useEffect(() => {
    if (open) setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0)
  }, [open, selectedIndex])

  // Keep the active option scrolled into view.
  useEffect(() => {
    if (!open || activeIndex < 0 || !listRef.current) return
    listRef.current.children[activeIndex]?.scrollIntoView({ block: 'nearest' })
  }, [open, activeIndex])

  function commit(index) {
    const opt = options[index]
    if (!opt || opt.disabled) return
    onChange(opt.value)
    setOpen(false)
  }

  function moveActive(dir) {
    setActiveIndex((i) => {
      let next = i
      for (let step = 0; step < options.length; step += 1) {
        next = (next + dir + options.length) % options.length
        if (!options[next]?.disabled) return next
      }
      return i
    })
  }

  function onKeyDown(e) {
    if (disabled) return
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault()
        setOpen(true)
      }
      return
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        moveActive(1)
        break
      case 'ArrowUp':
        e.preventDefault()
        moveActive(-1)
        break
      case 'Home':
        e.preventDefault()
        setActiveIndex(options.findIndex((o) => !o.disabled))
        break
      case 'End':
        e.preventDefault()
        setActiveIndex(options.length - 1)
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        commit(activeIndex)
        break
      case 'Escape':
        e.preventDefault()
        setOpen(false)
        break
      case 'Tab':
        setOpen(false)
        break
      default:
        // Type-ahead: jump to the first option starting with the typed run.
        if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
          const now = Date.now()
          const t = typeahead.current
          t.str = now - t.time > 600 ? e.key : t.str + e.key
          t.time = now
          const match = options.findIndex(
            (o) => !o.disabled && o.label.toLowerCase().startsWith(t.str.toLowerCase()),
          )
          if (match >= 0) setActiveIndex(match)
        }
    }
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        style={open ? { boxShadow: '0 0 0 3px rgba(60, 135, 247, 0.2)' } : undefined}
        className={`flex items-center border border-solid ${
          open ? 'border-primary' : 'border-line'
        } bg-surface font-medium text-ink transition focus:border-primary focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 ${
          small ? 'gap-x-1.5 rounded-[7px] px-2.5 py-[5px] text-[12.5px]' : 'gap-x-2 rounded-[9px] px-3 py-[9px] text-[14px]'
        } ${buttonClassName}`}
      >
        <span className={`flex-1 truncate text-left ${selected ? '' : 'text-faint'}`}>
          {selected ? selected.label : placeholder}
          {selected?.hint ? <span className="font-normal text-faint"> · {selected.hint}</span> : null}
        </span>
        <ChevronDownIcon
          className={`${small ? 'h-3.5 w-3.5' : 'h-4 w-4'} flex-none text-faint transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <ul
          ref={listRef}
          role="listbox"
          aria-label={ariaLabel}
          tabIndex={-1}
          style={{ boxShadow: '0 12px 32px rgba(15, 23, 42, 0.24)' }}
          className="absolute left-0 top-[calc(100%+6px)] z-50 max-h-[320px] w-max min-w-full max-w-[min(460px,80vw)] overflow-y-auto rounded-[10px] border border-solid border-line bg-surface p-1"
        >
          {options.map((opt, i) => {
            const isSelected = opt.value === value
            const isActive = i === activeIndex
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => commit(i)}
                className={`flex cursor-pointer items-center gap-2 rounded-[7px] px-2.5 ${
                  small ? 'py-1.5 text-[12.5px]' : 'py-2 text-[14px]'
                } ${
                  opt.disabled
                    ? 'cursor-not-allowed text-faint'
                    : isActive
                      ? 'bg-panel text-ink'
                      : 'text-ink'
                }`}
              >
                <span className="flex h-4 w-4 flex-none items-center justify-center text-primary">
                  {isSelected && (
                    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4" aria-hidden="true">
                      {/* coolicons: line/Check.svg */}
                      <path
                        d="M6 12L10.2426 16.2426L18.727 7.75732"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                <span className={`flex-1 ${isSelected ? 'font-semibold' : ''}`}>{opt.label}</span>
                {opt.hint ? <span className="flex-none pl-2 text-[12px] text-faint">{opt.hint}</span> : null}
                {opt.note ? (
                  <span className="flex-none rounded-full bg-panel px-1.5 py-[1px] text-[10.5px] font-semibold text-faint">
                    {opt.note}
                  </span>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
